import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Typography,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Alert,
  App,
  Popconfirm,
  Tooltip,
  Row,
  Col,
} from 'antd';
import {
  CloudServerOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  ThunderboltOutlined,
  EditOutlined,
  DeleteOutlined,
  GlobalOutlined,
  ApiOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  adminAPI,
  StorageConnector,
  StorageConnectorFormData,
} from '../services/adminAPI';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const PROVIDER_PRESETS: Record<string, Partial<StorageConnectorFormData>> = {
  'cloudflare-r2': {
    name: 'Cloudflare R2',
    endpoint: '',
    port: 443,
    useSSL: true,
    region: 'auto',
    bucketName: 'games',
    publicUrl: '',
  },
  'minio': {
    name: 'MinIO Local',
    endpoint: 'localhost',
    port: 9000,
    useSSL: false,
    region: 'auto',
    bucketName: 'games',
    publicUrl: '',
  },
  'aws-s3': {
    name: 'AWS S3',
    endpoint: 's3.us-east-1.amazonaws.com',
    port: 443,
    useSSL: true,
    region: 'us-east-1',
    bucketName: 'games',
    publicUrl: '',
  },
  'custom-s3': {
    name: 'Custom S3 Storage',
    endpoint: '',
    port: 443,
    useSSL: true,
    region: 'auto',
    bucketName: 'games',
    publicUrl: '',
  },
};

const SettingsStorage: React.FC = () => {
  const { message: messageApi } = App.useApp();
  const [connectors, setConnectors] = useState<StorageConnector[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingConnector, setEditingConnector] = useState<StorageConnector | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [testingInForm, setTestingInForm] = useState(false);

  const [form] = Form.useForm();

  useEffect(() => {
    loadConnectors();
  }, []);

  const loadConnectors = async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getStorageConnectors();
      if (res.success) {
        setConnectors(res.data);
      }
    } catch (err: any) {
      console.error('Failed to load storage connectors:', err);
      messageApi.error('Failed to load storage connectors');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddModal = () => {
    setEditingConnector(null);
    form.resetFields();
    // Default to Cloudflare R2
    form.setFieldsValue({
      ...PROVIDER_PRESETS['cloudflare-r2'],
      providerType: 'cloudflare-r2',
      isActive: connectors.length === 0, // auto active if first
    });
    setModalVisible(true);
  };

  const handleOpenEditModal = (connector: StorageConnector) => {
    setEditingConnector(connector);
    form.resetFields();
    form.setFieldsValue({
      name: connector.name,
      providerType: connector.providerType,
      endpoint: connector.endpoint,
      port: connector.port,
      useSSL: connector.useSSL,
      bucketName: connector.bucketName,
      region: connector.region,
      publicUrl: connector.publicUrl,
      accessKey: connector.accessKey,
      secretKey: '', // keep blank if not changing
      isActive: connector.isActive,
    });
    setModalVisible(true);
  };

  const handleProviderTypeChange = (type: string) => {
    const preset = PROVIDER_PRESETS[type];
    if (preset) {
      form.setFieldsValue({
        port: preset.port,
        useSSL: preset.useSSL,
        region: preset.region,
        bucketName: preset.bucketName,
        endpoint: form.getFieldValue('endpoint') || preset.endpoint,
      });
    }
  };

  const handleTestConnection = async (connector: StorageConnector) => {
    try {
      setTestingId(connector.id);
      messageApi.loading({ content: `Testing connection to ${connector.name}...`, key: 'test' });
      const res = await adminAPI.testStorageConnector({ id: connector.id });
      if (res.success) {
        messageApi.success({ content: res.message, key: 'test', duration: 4 });
      } else {
        messageApi.error({ content: res.message, key: 'test', duration: 5 });
      }
    } catch (err: any) {
      messageApi.error({
        content: err.response?.data?.message || 'Connection test failed',
        key: 'test',
        duration: 5,
      });
    } finally {
      setTestingId(null);
    }
  };

  const handleTestInForm = async () => {
    try {
      const values = await form.validateFields();
      if (!values.secretKey && editingConnector) {
        values.id = editingConnector.id;
      }
      setTestingInForm(true);
      messageApi.loading({ content: 'Testing credentials...', key: 'form-test' });
      const res = await adminAPI.testStorageConnector(values);
      if (res.success) {
        messageApi.success({ content: res.message, key: 'form-test', duration: 4 });
      } else {
        messageApi.error({ content: res.message, key: 'form-test', duration: 5 });
      }
    } catch (err: any) {
      messageApi.error({
        content: err.response?.data?.message || 'Validation error or connection failed',
        key: 'form-test',
        duration: 5,
      });
    } finally {
      setTestingInForm(false);
    }
  };

  const handleActivate = async (id: string) => {
    try {
      setActivatingId(id);
      const res = await adminAPI.activateStorageConnector(id);
      if (res.success) {
        messageApi.success(res.message);
        await loadConnectors();
      }
    } catch (err: any) {
      messageApi.error(err.response?.data?.message || 'Failed to activate connector');
    } finally {
      setActivatingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const res = await adminAPI.deleteStorageConnector(id);
      if (res.success) {
        messageApi.success(res.message);
        await loadConnectors();
      }
    } catch (err: any) {
      messageApi.error(err.response?.data?.message || 'Failed to delete connector');
    }
  };

  const handleSaveForm = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      if (editingConnector) {
        const res = await adminAPI.updateStorageConnector(editingConnector.id, values);
        if (res.success) {
          messageApi.success(res.message);
          setModalVisible(false);
          await loadConnectors();
        }
      } else {
        const res = await adminAPI.createStorageConnector(values);
        if (res.success) {
          messageApi.success(res.message);
          setModalVisible(false);
          await loadConnectors();
        }
      }
    } catch (err: any) {
      messageApi.error(err.response?.data?.message || 'Failed to save connector');
    } finally {
      setSaving(false);
    }
  };

  const getProviderTag = (type: string) => {
    switch (type) {
      case 'cloudflare-r2':
        return <Tag color="orange">Cloudflare R2</Tag>;
      case 'minio':
        return <Tag color="blue">MinIO</Tag>;
      case 'aws-s3':
        return <Tag color="green">AWS S3</Tag>;
      default:
        return <Tag color="purple">S3 Compatible</Tag>;
    }
  };

  const columns = [
    {
      title: 'Connector Name',
      key: 'name',
      render: (_: any, record: StorageConnector) => (
        <Space direction="vertical" size={2}>
          <Space>
            <Text strong>{record.name}</Text>
            {getProviderTag(record.providerType)}
          </Space>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Bucket: <Text code>{record.bucketName}</Text>
          </Text>
        </Space>
      ),
    },
    {
      title: 'Endpoint & Domain',
      key: 'endpoint',
      render: (_: any, record: StorageConnector) => (
        <Space direction="vertical" size={2}>
          <Text style={{ fontSize: '13px' }}>
            <ApiOutlined /> {record.endpoint}:{record.port} {record.useSSL ? '(SSL)' : ''}
          </Text>
          {record.publicUrl ? (
            <Text type="success" style={{ fontSize: '12px' }}>
              <GlobalOutlined /> CDN: {record.publicUrl}
            </Text>
          ) : (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Direct S3 URL
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Access Key',
      key: 'accessKey',
      render: (_: any, record: StorageConnector) => (
        <Space direction="vertical" size={2}>
          <Text code>{record.accessKey}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Secret: {record.secretKeyMasked || '••••••••'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      key: 'isActive',
      width: 140,
      render: (_: any, record: StorageConnector) => (
        record.isActive ? (
          <Tag color="success" icon={<CheckCircleOutlined />} style={{ padding: '4px 8px' }}>
            ACTIVE (Default)
          </Tag>
        ) : (
          <Tag color="default">INACTIVE</Tag>
        )
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 260,
      render: (_: any, record: StorageConnector) => (
        <Space size="small">
          <Button
            size="small"
            icon={<ThunderboltOutlined />}
            loading={testingId === record.id}
            onClick={() => handleTestConnection(record)}
          >
            Test
          </Button>

          {!record.isActive && (
            <Button
              size="small"
              type="primary"
              loading={activatingId === record.id}
              onClick={() => handleActivate(record.id)}
            >
              Set Active
            </Button>
          )}

          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenEditModal(record)}
          />

          {!record.isActive && (
            <Popconfirm
              title="Delete Connector"
              description={`Are you sure you want to delete ${record.name}?`}
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <CloudServerOutlined /> Storage Providers
          </Title>
          <Paragraph type="secondary" style={{ margin: '4px 0 0' }}>
            Manage Cloudflare R2 and S3-compatible storage providers for HTML5 games and media assets.
          </Paragraph>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenAddModal}>
          Add Storage Provider
        </Button>
      </div>

      <Alert
        message="Cloudflare R2 Integration Guide"
        description={
          <div>
            To connect <strong>Cloudflare R2</strong>: Go to Cloudflare Dashboard &gt; <strong>R2 Object Storage</strong> &gt; <strong>Manage R2 API Tokens</strong> &gt; Create API Token with <em>Object Read &amp; Write</em> permission. Set the endpoint to <Text code>&lt;account-id&gt;.r2.cloudflarestorage.com</Text> with port <Text code>443</Text> and SSL enabled. Connect a Custom Domain (e.g. <Text code>games.yourdomain.com</Text>) for <strong>0$ Egress bandwidth</strong>!
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 20 }}
      />

      <Card>
        <Table
          dataSource={connectors}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={false}
          locale={{ emptyText: 'No storage providers configured yet. Add your Cloudflare R2 or MinIO connection!' }}
        />
      </Card>

      <Modal
        title={editingConnector ? `Edit Storage Provider: ${editingConnector.name}` : 'Add Storage Provider'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={680}
        footer={[
          <Button key="test" loading={testingInForm} onClick={handleTestInForm}>
            Test Connection
          </Button>,
          <Button key="cancel" onClick={() => setModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" loading={saving} onClick={handleSaveForm}>
            Save Connector
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical" initialValues={{ port: 443, useSSL: true, region: 'auto' }}>
          <Row gutter={16}>
            <Col span={14}>
              <Form.Item
                name="name"
                label="Connector Name"
                rules={[{ required: true, message: 'Please enter a name' }]}
              >
                <Input placeholder="e.g., Cloudflare R2 Production" />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item
                name="providerType"
                label="Provider Preset"
                rules={[{ required: true, message: 'Select provider type' }]}
              >
                <Select onChange={handleProviderTypeChange}>
                  <Option value="cloudflare-r2">Cloudflare R2</Option>
                  <Option value="minio">MinIO (Self-hosted)</Option>
                  <Option value="aws-s3">AWS S3</Option>
                  <Option value="custom-s3">Custom S3 Compatible</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={18}>
              <Form.Item
                name="endpoint"
                label="S3 Endpoint (Hostname only)"
                rules={[{ required: true, message: 'Please enter the S3 endpoint' }]}
                tooltip="Do not include http:// or https:// (e.g., xxxxxx.r2.cloudflarestorage.com or localhost)"
              >
                <Input placeholder="xxxxxx.r2.cloudflarestorage.com" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="port" label="Port">
                <InputNumber style={{ width: '100%' }} placeholder="443" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="bucketName"
                label="Bucket Name"
                rules={[{ required: true, message: 'Please enter bucket name' }]}
              >
                <Input placeholder="games" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="region" label="Region">
                <Input placeholder="auto (or us-east-1)" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="useSSL" label="SSL" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="publicUrl"
            label="Public CDN / Custom Domain URL (Recommended for R2)"
            tooltip="If you connected a custom domain in Cloudflare R2 (e.g. https://games.iwin.me), enter it here. Games will load directly from this CDN domain with 0$ egress!"
          >
            <Input placeholder="https://games.iwin.me (optional)" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="accessKey"
                label="Access Key ID"
                rules={[{ required: true, message: 'Please enter Access Key ID' }]}
              >
                <Input placeholder="Access Key ID" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="secretKey"
                label="Secret Access Key"
                rules={[{ required: !editingConnector, message: 'Please enter Secret Access Key' }]}
                tooltip={editingConnector ? 'Leave blank to keep existing Secret Key' : undefined}
              >
                <Input.Password placeholder={editingConnector ? 'Leave blank to keep unchanged' : 'Secret Access Key'} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="isActive"
            label="Set as Active Storage Provider"
            valuePropName="checked"
            extra="When active, all new game uploads will automatically be stored on this provider."
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SettingsStorage;
